import { define, html, store, router } from "hybrids";

import layout from "~/factories/layout";
import socketio from "~/factories/socketio";

import Broadcast, { addLikes } from "~/stores/Broadcast";
import User from "~/stores/User";
import Payment from "~/stores/Payment";
import Comment from "~/stores/Comment";
import Session from "~/stores/Session";

import { VBroadcastContainer } from "../elements/Container";

import Chat from "./Chat";
import Share from "./Share";
import People from "./People";

import Donate from "./donate/Root";
import UserView from "./User";
import { Socket } from "socket.io-client";
import PaymentMethods from "~/stores/PaymentMethods";

function setDelayedTimeout(fn: Function, maxDelay = 5 * 1000) {
  return setTimeout(fn, Math.random() * maxDelay);
}

export interface VBroadcastRoot extends HTMLElement {
  broadcastId: string;
  broadcast: Broadcast;
  session: Session;
  payments: PaymentMethods;
  registrationId: string;
  views: HTMLElement[];
  socketUrl: string;
  accessToken: string;
  socket: Socket | null;
  unreadComments: boolean;

  videoId: string;

  favorite: boolean;
}

function countLikes(host: VBroadcastRoot) {
  const { broadcast, socket } = host;

  addLikes(broadcast);

  if (socket) {
    socket.emit("likes:update", { likes: 1 });
  }
}

function countLikesInfinite(host: VBroadcastRoot, event: Event) {
  const el = event.target as HTMLElement;
  const interval = setInterval(() => countLikes(host), 250);

  const preventDefault = (e: Event) => {
    e.preventDefault();
  };

  const clearCb = () => {
    clearInterval(interval);
    window.removeEventListener("contextmenu", preventDefault);
    el.removeEventListener("mouseup", clearCb);
    el.removeEventListener("mouseleave", clearCb);
    el.removeEventListener("touchend", clearCb);
  };

  window.addEventListener("contextmenu", preventDefault);
  el.addEventListener("mouseup", clearCb);
  el.addEventListener("mouseleave", clearCb);
  el.addEventListener("touchend", clearCb);
}

function handleChatClick(host: VBroadcastRoot, event: Event) {
  host.unreadComments = false;

  if (router.active(Chat)) {
    event.preventDefault();

    const el = host.querySelector(
      "v-broadcast-container[stick-to-bottom]"
    ) as VBroadcastContainer;

    el?.scrollToBottom();
  }
}

export default define<VBroadcastRoot>({
  tag: "v-broadcast-root",
  broadcastId: "",
  broadcast: store(Broadcast, { id: "broadcastId" }),
  session: store(Session),
  payments: store(PaymentMethods, {
    id: ({ broadcast }) =>
      store.ready(broadcast) ? broadcast?.course.owner.id : undefined,
  }),
  videoId: ({ broadcast }) => `/b/${broadcast.id}`,
  registrationId: "",
  render: layout("flex"),
  views: router(People, { params: ["broadcast"] }),
  unreadComments: false,
  socketUrl: "",
  accessToken: "",
  socket: socketio(async (host, socket) => {
    socket.on("users:list", async (connected: [User]) => {
      console.log("socket event: 'users:list'");

      const broadcast = await store.resolve(host.broadcast);
      store.sync(broadcast, { connected });
    });

    socket.on("users:create", async (user: User) => {
      console.log("socket event: 'users:create'");

      const broadcast = await store.resolve(host.broadcast);
      const { users } = broadcast;

      if (!users.find((u) => u.id === user.id)) {
        store.sync(broadcast, {
          users: users.concat(user),
        });
      }
    });

    socket.on("users:connect", async (user: User) => {
      console.log("socket event: 'users:connect'");

      const broadcast = await store.resolve(host.broadcast);
      const { connected } = broadcast;

      store.sync(broadcast, {
        connected: connected.concat(user),
      });
    });

    socket.on("users:disconnect", async (user: User) => {
      console.log("socket event: 'users:disconnect'");

      const broadcast = await store.resolve(host.broadcast);
      const index = broadcast.connected.findIndex((u) => u.id === user.id);

      if (index > -1) {
        const connected = broadcast.connected.slice();
        connected.splice(index, 1);

        store.sync(broadcast, { connected });
      }
    });

    socket.on("users:revoke", async () => {
      console.log("socket event: 'users:revoke'");

      const broadcast = await store.resolve(host.broadcast);
      const targetURL = `/b/${broadcast.id}/access_removed?registration_uuid=${host.registrationId}`;

      setDelayedTimeout(() => {
        window.location.replace(targetURL);
      });
    });

    socket.on("likes:update", async (values: { likes: number }) => {
      console.log("socket event: 'likes:update'");

      const broadcast = await store.resolve(host.broadcast);
      store.sync(broadcast, {
        likes: broadcast.likes + values.likes,
      });
    });

    socket.on("comments:create", async (comment: Comment) => {
      console.log("socket event: 'comments:create'");
      const broadcast = await store.resolve(host.broadcast);
      const session = await store.resolve(host.session);

      if (session.blockedUsers.find((u) => u == comment.user)) {
        return;
      }

      if (!broadcast.comments.find((c) => c.id === comment.id)) {
        store.sync(broadcast, {
          comments: [comment].concat(broadcast.comments),
        });

        if (!router.active(Chat, { stack: true })) {
          host.unreadComments = true;
        }
      }
    });

    socket.on("comments:update", async (values: Comment) => {
      console.log("socket event: 'comments:update'");
      const comment = store.get(Comment, values.id);

      if (store.ready(comment)) {
        const { id, ...fields } = values;
        store.sync(comment, fields);
      }
    });

    socket.on("comments:delete", async (values: Comment) => {
      console.log("socket event: 'comments:delete'");
      const broadcast = await store.resolve(host.broadcast);

      const comment = store.get(Comment, values.id);

      if (store.ready(comment)) {
        store.sync(broadcast, {
          comments: broadcast.comments.filter(
            (c) => c !== comment && c.parent !== comment
          ),
        });
        store.sync(comment, null);
      }
    });

    socket.on("payments:create", async (payment: Payment) => {
      console.log("socket event: 'payments:create'");

      const broadcast = await store.resolve(host.broadcast);
      store.sync(broadcast, {
        payments: broadcast.payments.concat(payment),
      });
    });

    socket.on("page:refresh", async () => {
      console.log("socket event: 'page:refresh'");
      setDelayedTimeout(() => {
        window.location.reload();
      });
    });

    socket.on("connect_error", console.error);
  }),

  content: ({ broadcast, payments, views, unreadComments, session, videoId, favorite }) => html`
    <v-broadcast-layout
      sponsors="${router.active([People, Chat]) &&
      store.ready(broadcast) &&
      broadcast.course.sponsors.length}"
    >
      ${store.ready(broadcast) &&
      html`
        <v-broadcast-player
          slot="player"
          src="${broadcast.src}"
          startAt="${broadcast.event.startsAt}"
          owner="${broadcast.course.owner}"
          availableUntil="${broadcast.availableUntil}"
          poster="${broadcast.course.poster}"
          iframelyKey="${broadcast.iframely}"
          muxDataApiKey="${broadcast.muxDataApiKey}"
          videoId="${videoId}"
          videoTitle="${broadcast.course.name}"
          viewerUserId="${store.ready(session) && session.user.id}"
        ></v-broadcast-player>
      `}
        
      ${(window.innerWidth > 921 || !router.active(Chat)) && html`
        <v-ui-text variant="h1" selectable slot="title">
          ${store.ready(broadcast) &&
          html`
            <v-ui-link href="${broadcast.course.url}">
              ${broadcast.course.name}
            </v-ui-link>
          `}
        </v-ui-text>
        
        <v-ui-flexbox flow="row" slot="title">
            <v-ui-button
              href="${router.url(Share)}"
              variant="icon"
              icon="share"
              name="Share"
              justify="right"
              active
            ></v-ui-button>
          ${broadcast.permissions.canManageAllComments &&
            html`<v-ui-button
              variant="icon"
              icon="edit"
              href="/management/courses/${broadcast.course.id}/broadcasts"
              name="Edit"
              justify="right"
              active
            ></v-ui-button>`
          }
          </v-ui-flexbox>
      `}
      
      ${store.ready(broadcast) &&
      broadcast.course.sponsors.map(
        (sponsor) =>
          html`<v-ui-link
            href="${router.url(UserView, { user: sponsor.profile })}"
            block
            slot="sponsors"
          >
            <v-broadcast-sponsor-logo
              src="${sponsor.image}"
              alt="${sponsor.name}"
            ></v-broadcast-sponsor-logo>
          </v-ui-link>`
      )}
      
      <v-ui-flexbox flow="row" grow="1">${views}</v-ui-flexbox>
      
      <div>
        ${store.ready(broadcast) &&
        !router.active([Donate]) &&
        html`
          <v-broadcast-actions shadow="${router.active(People)}">
            ${store.ready(payments) &&
            !payments.disabled &&
            html`<v-ui-button
              href="${router.url(Donate)}"
              icon="donate"
              name="Donate"
              color="primary"
            ></v-ui-button>`}
            <v-broadcast-clap-bubbles likes="${broadcast.likes}">
              <v-ui-button
                title="Applause"
                icon="applause"
                onmousedown="${countLikesInfinite}"
                ontouchstart="${countLikesInfinite}"
                onclick="${countLikes}"
              ></v-ui-button>
            </v-broadcast-clap-bubbles>
            <v-ui-button
              href="${router.url(People)}"
              variant="icon"
              icon="people"
              name="People"
              active="${router.active(People)}"
              slot="info-chat"
            ></v-ui-button>
            <v-ui-button
              href="${router.url(Chat)}"
              variant="icon"
              icon="chat"
              name="Chat"
              active="${router.active(Chat)}"
              dot="${unreadComments}"
              onclick="${handleChatClick}"
              slot="info-chat"
            ></v-ui-button>
          </v-broadcast-actions>
        `}
      </div>
    </v-broadcast-layout>
  `,
});
